import * as React from "react";
import {
  Box,
  Stack,
  Button,
  Chip,
  Typography,
  Paper,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Sidebar from "../Dashboard/Sidebar.js";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { enquiryService } from "../../services/enquiryService";

const RootContainer = styled(Box)({
  background: "#F8FAFC",
  minHeight: "100vh",
  display: "flex",
});

const ContentArea = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  marginLeft: "18vw",
  padding: "40px",
  width: "calc(100% - 18vw)",
  transition: "all 0.3s ease",
  [theme.breakpoints.down("md")]: {
    marginLeft: 0,
    width: "100%",
    padding: "20px",
    paddingTop: "84px",
  },
}));

const GlassCard = styled(Paper)({
  padding: "24px",
  borderRadius: "16px",
  background: "#FFFFFF",
  border: "1px solid #E2E8F0",
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
});

const Enquiries = () => {
  const [enquiries, setEnquiries] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [updatingId, setUpdatingId] = React.useState(null);
  const isSuperAdmin = localStorage.getItem("superadmin") === "true";

  const fetchEnquiries = async () => {
    setIsLoading(true);
    setError("");
    try {
      const result = await enquiryService.getEnquiries();
      if (!result || result.success === false) {
        setError(result?.message || "Failed to load enquiries.");
        setEnquiries([]);
      } else {
        const list = Array.isArray(result.data)
          ? result.data
          : Array.isArray(result.data?.data)
          ? result.data.data
          : [];

        const sorted = [...list].sort((a, b) => {
          const aDate = new Date(a.created_at || a.createdAt || a.createdDate);
          const bDate = new Date(b.created_at || b.createdAt || b.createdDate);
          return bDate - aDate;
        });

        setEnquiries(sorted);
      }
    } catch (e) {
      console.error(e);
      setError("Failed to load enquiries.");
      setEnquiries([]);
    } finally {
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    fetchEnquiries();
  }, []);

  const handleMarkResolved = async (id) => {
    if (!id) return;
    setUpdatingId(id);
    try {
      const result = await enquiryService.updateEnquiryStatus(id, "resolved");
      if (!result || result.success === false) {
        toast.error(result?.message || "Failed to update enquiry.");
      } else {
        setEnquiries((prev) =>
          prev.map((enquiry) =>
            enquiry.id === id ? { ...enquiry, status: "resolved" } : enquiry,
          ),
        );
        toast.success("Enquiry marked as resolved.");
        // Notify sidebar to refresh badge count
        window.dispatchEvent(new Event("enquiriesUpdated"));
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to update enquiry.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDeleteEnquiry = async (id) => {
    if (!id) return;
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this enquiry?",
    );
    if (!confirmDelete) return;

    setUpdatingId(id);
    try {
      const result = await enquiryService.deleteEnquiry(id);
      if (!result || result.success === false) {
        toast.error(result?.message || "Failed to delete enquiry.");
      } else {
        setEnquiries((prev) => prev.filter((enquiry) => enquiry.id !== id));
        toast.success("Enquiry deleted successfully.");
        window.dispatchEvent(new Event("enquiriesUpdated"));
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to delete enquiry.");
    } finally {
      setUpdatingId(null);
    }
  };

  const renderStatusChip = (status) => {
    const normalized = (status || "").toLowerCase();

    if (normalized === "resolved") {
      return (
        <Chip
          label="Resolved"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            backgroundColor: "#DCFCE7",
            color: "#166534",
            border: "1.5px solid #16A34A",
          }}
        />
      );
    }

    if (normalized === "contacted") {
      return (
        <Chip
          label="Contacted"
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.75rem",
            backgroundColor: "#DBEAFE",
            color: "#1D4ED8",
            border: "1.5px solid #3B82F6",
          }}
        />
      );
    }

    // Default: New / Pending
    return (
      <Chip
        label={normalized === "pending" ? "Pending" : "New"}
        size="small"
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          backgroundColor: "#FFEDD5",
          color: "#C05621",
          border: "1.5px solid #F97316",
        }}
      />
    );
  };

  const renderEnquiryDetails = (raw) => {
    let parsed = null;

    if (raw && typeof raw === "string") {
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        parsed = null;
      }
    } else if (raw && typeof raw === "object") {
      parsed = raw;
    }

    if (!parsed || typeof parsed !== "object") {
      return (
        <Typography
          variant="body2"
          sx={{ fontSize: "0.9rem" }}
          className="wrap-text"
        >
          {raw || "-"}
        </Typography>
      );
    }

    const entries = Object.entries(parsed);

    if (!entries.length) {
      return (
        <Typography
          variant="body2"
          sx={{ fontSize: "0.9rem" }}
          className="wrap-text"
        >
          {raw || "-"}
        </Typography>
      );
    }

    return (
      <Stack
        direction="row"
        spacing={0.75}
        useFlexGap
        flexWrap="wrap"
        sx={{ maxWidth: "100%" }}
      >
        {entries.map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${
              value && typeof value === "object"
                ? JSON.stringify(value)
                : String(value)
            }`}
            size="small"
            sx={{
              maxWidth: "100%",
              borderRadius: "999px",
              backgroundColor: "#F1F5F9",
              color: "#111827",
              fontSize: "0.7rem",
              px: 0.75,
              py: 0,
            }}
          />
        ))}
      </Stack>
    );
  };

  return (
    <RootContainer>
      <Sidebar />
      <ContentArea>
        {/* HEADER */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          spacing={3}
          sx={{ mb: 4 }}
        >
          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#1E293B",
                fontFamily: "Kamerik",
                letterSpacing: "-1.5px",
                fontSize: { xs: "2rem", md: "3rem" },
              }}
            >
              Enquiries
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748B", fontWeight: 500 }}
            >
              Manage and track all customer enquiries in one place.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              px: 2.5,
              py: 1.5,
              borderRadius: "999px",
              backgroundColor: "#EEF2FF",
            }}
          >
            <PhoneInTalkIcon sx={{ color: "#4F46E5" }} />
            <Typography
              variant="body2"
              sx={{ color: "#4F46E5", fontWeight: 600 }}
            >
              New enquiries:{" "}
              {enquiries.filter(
                (e) => (e.status || "").toLowerCase() === "new",
              ).length || 0}
            </Typography>
          </Stack>
        </Stack>

        <GlassCard elevation={0}>
          {error && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="body2"
                sx={{ color: "#B91C1C", fontWeight: 500 }}
              >
                {error}
              </Typography>
            </Box>
          )}

          {isLoading ? (
            <Box
              sx={{
                minHeight: "40vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#64748B", fontWeight: 500 }}
              >
                Loading enquiries...
              </Typography>
            </Box>
          ) : !isLoading && enquiries.length === 0 ? (
            <Box
              sx={{
                minHeight: "40vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{ color: "#64748B", fontWeight: 500 }}
              >
                No enquiries yet.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {enquiries.map((item) => {
                const createdAtValue =
                  item.created_at || item.createdAt || item.createdDate;
                let createdAtFormatted = "-";
                if (createdAtValue) {
                  const date = new Date(createdAtValue);
                  createdAtFormatted = Number.isNaN(date.getTime())
                    ? createdAtValue
                    : date.toLocaleString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      });
                }

                const normalizedStatus = (item.status || "").toLowerCase();
                const isResolved = normalizedStatus === "resolved";

                let statusAccentColor = "#E5E7EB";
                if (normalizedStatus === "resolved") {
                  statusAccentColor = "#16A34A"; // green
                } else if (
                  normalizedStatus === "pending" ||
                  normalizedStatus === "new"
                ) {
                  statusAccentColor = "#EF4444"; // red
                } else if (normalizedStatus === "contacted") {
                  statusAccentColor = "#3B82F6"; // blue
                }

                return (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.75,
                        borderRadius: "12px",
                        border: "1px solid #E5E7EB",
                        backgroundColor: "#FFFFFF",
                        height: "100%",
                        position: "relative",
                        overflow: "hidden",
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.04)",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          inset: 0,
                          borderTop: `3px solid ${statusAccentColor}`,
                          borderRadius: "inherit",
                          pointerEvents: "none",
                        },
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 10px 25px rgba(15, 23, 42, 0.12)",
                        },
                      }}
                    >
                      <Stack spacing={1.25} sx={{ height: "100%" }}>
                        <Stack
                          direction={{ xs: "column", sm: "row" }}
                          justifyContent="space-between"
                          alignItems={{ xs: "flex-start", sm: "center" }}
                          spacing={1.25}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              sx={{
                                width: 26,
                                height: 26,
                                borderRadius: "999px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "#EFF6FF",
                                color: "#1D4ED8",
                                fontSize: "0.75rem",
                              }}
                            >
                              <PhoneInTalkIcon sx={{ fontSize: 16 }} />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#6B7280",
                                  textTransform: "uppercase",
                                  fontSize: "0.65rem",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Contact
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: "#111827",
                                  fontSize: "0.95rem",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                {item.phone || "-"}
                              </Typography>
                            </Box>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#6B7280", fontSize: "0.75rem" }}
                            >
                              {createdAtFormatted}
                            </Typography>
                            {renderStatusChip(item.status)}
                          </Stack>
                        </Stack>

                        {renderEnquiryDetails(item.enquiry)}

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          sx={{ mt: "auto" }}
                        >
                          {isSuperAdmin && (
                            <Button
                              variant="text"
                              size="small"
                              startIcon={<DeleteOutlineIcon />}
                              color="error"
                              disabled={updatingId === item.id}
                              onClick={() => handleDeleteEnquiry(item.id)}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": {
                                  backgroundColor: "#FEF2F2",
                                },
                              }}
                            >
                              Delete
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CheckCircleIcon />}
                            disabled={isResolved || updatingId === item.id}
                            onClick={() => handleMarkResolved(item.id)}
                            sx={{
                              borderRadius: "8px",
                              textTransform: "none",
                              borderColor: "#16A34A",
                              color: isResolved ? "#9CA3AF" : "#16A34A",
                              "&:hover": {
                                borderColor: "#15803D",
                                bgcolor: "#ECFDF5",
                              },
                            }}
                          >
                            {isResolved ? "Resolved" : "Mark as Resolved"}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </GlassCard>

        <ToastContainer position="bottom-right" />
      </ContentArea>
    </RootContainer>
  );
};

export default Enquiries;
